import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { AUTH_ROUTES, auth0Config, AUTH_SCOPES } from '@feature/auth/constants/auth.constants';
import { useErrorHandler } from '@shared/hooks/useErrorHandler';
import { clearAuthApiFailure } from '@shared/utils/authApiFailure';

/**
 * Limpia el almacenamiento local del navegador relacionado con Auth0
 * Nota: Las cookies de Auth0 en el dominio de Auth0 solo pueden ser eliminadas
 * mediante el endpoint de logout de Auth0, no desde JavaScript por Same-Origin Policy
 */
const clearAuth0Storage = () => {
  try {
    // Limpiar localStorage
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('auth0') || key.includes('@@auth0spajs') || key.includes('@@auth0spajs::'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        console.warn(`Error al eliminar clave de localStorage: ${key}`, e);
      }
    });

    // Limpiar sessionStorage
    const sessionKeysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.includes('auth0') || key.includes('@@auth0spajs') || key.includes('@@auth0spajs::'))) {
        sessionKeysToRemove.push(key);
      }
    }
    sessionKeysToRemove.forEach(key => {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {
        console.warn(`Error al eliminar clave de sessionStorage: ${key}`, e);
      }
    });

    // Limpiar cookies del dominio de la aplicación (solo las que podemos controlar)
    const appCookies = document.cookie.split(';');
    appCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      if (cookieName.includes('auth0') || cookieName.includes('auth0spajs')) {
        // Intentar eliminar con diferentes configuraciones de dominio
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
        const domainParts = window.location.hostname.split('.');
        if (domainParts.length > 1) {
          const parentDomain = '.' + domainParts.slice(-2).join('.');
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${parentDomain}`;
        }
      }
    });
  } catch (error) {
    console.warn('Error al limpiar almacenamiento de Auth0:', error);
  }
};

const useRealAuth = () => {
  const {
    isAuthenticated,
    isLoading,
    user,
    error,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const { handleError } = useErrorHandler();

  const login = useCallback(async (customReturnTo?: string) => {
    try {
      const currentUrl = window.location.pathname + window.location.search;
      const returnTo = customReturnTo || (currentUrl === '/login' ? AUTH_ROUTES.HOME : currentUrl);
      
      await loginWithRedirect({
        appState: {
          returnTo: returnTo
        }
      });
    } catch (error) {
      handleError(error, { showNotification: false, logToConsole: true });
    }
  }, [loginWithRedirect, handleError]);

  const logout = useCallback(async () => {
    try {
      clearAuthApiFailure();
      // Limpiar almacenamiento local primero (esto se puede hacer desde JavaScript)
      clearAuth0Storage();

      // Usar logout de Auth0 con configuración para cerrar sesión completamente
      // Por defecto, Auth0 redirige al servidor para limpiar cookies (no es localOnly)
      // federated: true - Cierra sesión en todos los proveedores federados (SSO)
      await auth0Logout({
        logoutParams: {
          returnTo: window.location.origin,
          federated: true, // Cierra sesión en proveedores federados (Google, Facebook, etc.)
        },
      });

      // No necesitamos setTimeout ni redirección manual porque Auth0 lo maneja
      // El logout de Auth0 redirige automáticamente a returnTo después de limpiar la sesión

    } catch (error) {
      handleError(error, { showNotification: false, logToConsole: true });
      
      // En caso de error, limpiar lo que podamos y redirigir
      clearAuth0Storage();
      
      // Forzar redirección completa para limpiar cualquier estado residual
      window.location.href = window.location.origin;
    }
  }, [auth0Logout, handleError]);

  const getToken = useCallback(async () => {
    try {
      // Pasar explícitamente audience y scopes para asegurar que el token incluya los scopes correctos
      return await getAccessTokenSilently({
        authorizationParams: {
          audience: auth0Config.audience,
          scope: AUTH_SCOPES
        }
      });
    } catch (error) {
      handleError(error, { showNotification: false, logToConsole: true });
      return null;
    }
  }, [getAccessTokenSilently, handleError]);

  if (error) {
    handleError(error, { showNotification: false, logToConsole: true });
  }

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    login,
    logout,
    getToken,
  };
};

export const useAuth = useRealAuth;